import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import {
  SVT_CORNER,
  SVT_FOCUS_RING,
  SVT_RADIUS,
  SVT_TRANSITION,
} from '@eliadmin/core/src/components/ui/svetech-shape';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

/*
 * SveTech look of the core button (Key design „Primárne tlačidlo“, same as `svetech/ui/button`):
 * `default`/`accent` = the yellow button with its shadow, the other variants are flat. Same API,
 * variants, sizes and `ea-*` classes as the core. Height 48 → 50 px of the default size comes from
 * a variable, so a consumer's `h-*` replaces it at every breakpoint.
 */
const PRIMARY =
  'bg-[#f1c40f] text-[#14171a] shadow-[0_8px_32px_rgba(241,196,15,0.4)] hover:bg-[#f7d63e] active:bg-[#d9ae05]';
const PRIMARY_SOFT = 'bg-[#fdf4ce] text-[#14171a] hover:bg-[#f1c40f]/40';

const buttonVariants = cva(
  [
    'svt-button relative inline-flex flex-row items-center justify-center space-x-2',
    SVT_CORNER,
    SVT_TRANSITION,
    SVT_FOCUS_RING,
    'disabled:pointer-events-none disabled:opacity-70',
    'ea-button',
  ],
  {
    variants: {
      variant: {
        default: [PRIMARY, 'ea-button--default'],
        destructive: ['bg-[#b3261e] text-white hover:bg-[#b3261e]/90', 'ea-button--destructive'],
        accent: [PRIMARY, 'ea-button--accent'],
        outline: [
          'border border-[#dfe2db] bg-transparent text-[#14171a] hover:border-[#c9cdc4] hover:bg-[#f2f3ef]',
          'ea-button--outline',
        ],
        secondary: ['bg-[#f2f3ef] text-[#14171a] hover:bg-[#e6e8e2]', 'ea-button--secondary'],
        ghost: ['text-[#14171a] hover:bg-[#f2f3ef]', 'ea-button--ghost'],
        link: [
          'text-[#2c7a25] underline-offset-4 hover:text-[#123410] hover:underline',
          'ea-button--link',
        ],
        dark: ['bg-[#14171a] text-white hover:bg-[#14171a]/90', 'ea-button--dark'],
        rating: ['bg-rating/20 text-rating hover:bg-rating/25'],

        'default-alt': [PRIMARY_SOFT],
        'destructive-alt': ['bg-[#fdecea] text-[#b3261e] hover:bg-[#b3261e]/15'],
        'accent-alt': [PRIMARY_SOFT],
      },
      size: {
        default: [
          'h-[var(--svt-button-h)] px-4 [--svt-button-h:48px] md:[--svt-button-h:50px]',
          SVT_RADIUS,
          'ea-button--size-default',
        ],
        xs: ['h-8 px-3 text-xs [--svt-radius:8px]', 'ea-button--size-xs'],
        sm: ['h-10 px-4 text-sm [--svt-radius:12px]', 'ea-button--size-sm'],
        lg: ['h-14 px-8', SVT_RADIUS, 'ea-button--size-lg'],
        icon: ['h-10 w-10 [--svt-radius:12px]', 'ea-button--size-icon'],
        'icon-sm': ['h-7 w-7 [--svt-radius:8px]', 'ea-button--size-icon-sm'],
        'icon-xs': ['h-5 w-5 [--svt-radius:5px]', 'ea-button--size-icon-xs'],
        'icon-lg': ['h-12 w-12', SVT_RADIUS, 'ea-button--size-icon-lg'],
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, isLoading, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
        disabled={props.disabled || isLoading}
        aria-disabled={props.disabled || isLoading}
      >
        {isLoading && !asChild ? (
          // The label stays in the flow (invisible) so the button keeps its width while loading.
          <>
            <span className="invisible inline-flex items-center space-x-2">{children}</span>
            <span className="absolute inset-0 flex items-center justify-center">
              <Spinner />
            </span>
          </>
        ) : isLoading ? (
          <Spinner />
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
